import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Skeleton, Stack, Typography } from "@mui/material";
import { getOrParseDateTime } from "@cpt/shared";
import useAsyncRequest from "hooks/useAsyncRequest";
import { getErrorMessage } from "utils/errorHelpers";
import { RateCardAssignmentStatus } from "client/api-v2";
import { AlertSeverity, useAlerts } from "components/AlertProvider/AlertProvider";
import { DetailsPageLayout } from "components/DetailsPageLayout/DetailsPageLayout";
import { PolicyAuthorized } from "components/authorization/PolicyAuthorized";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { CreateRateCardDialog } from "areas/rate-cards/components/CreateRateCardDialog";
import { activateDraftRateCardAssignment } from "areas/rate-cards/services/rateCardService";
import { SubscriptionCurrentRateCard } from "../components/SubscriptionCurrentRateCard";
import { SubscriptionRateCardAssignmentHistory } from "../components/SubscriptionRateCardAssignmentHistory";
import { SubscriptionRateCardEmptyState } from "../components/SubscriptionRateCardEmptyState";
import { getRateCardAssignmentHistory } from "../services/subscriptionService";
import { subscriptionIdRouteParam } from "../subscription-routes";

export function SubscriptionRateCard() {
  const { [subscriptionIdRouteParam]: subscriptionId } = useParams<Record<string, string>>();
  const { sendNewAlert } = useAlerts();
  const { data: assignments, loading, error, refresh } = useAsyncRequest(getRateCardAssignmentHistory, subscriptionId);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [activating, setActivating] = useState(false);

  // An activation stays in flight until the refetched history lands: refresh() only
  // schedules a throttled refetch, so clearing the flag any sooner would re-enable the
  // draft's Activate button while the pre-activation card is still on screen.
  useEffect(() => {
    setActivating(false);
  }, [assignments]);

  const notImplemented = () => sendNewAlert("Not implemented yet", AlertSeverity.Info);

  const active = assignments?.find((assignment) => assignment.status === RateCardAssignmentStatus.Active);

  const draft = (assignments ?? [])
    .filter((assignment) => assignment.status === RateCardAssignmentStatus.Draft)
    .sort((a, b) => getOrParseDateTime(b.createdDate).toMillis() - getOrParseDateTime(a.createdDate).toMillis())
    .at(0);

  const hasAssignment = Boolean(active ?? draft);

  const onActivateDraft = async () => {
    if (!subscriptionId || !draft) return;

    setActivating(true);
    try {
      await activateDraftRateCardAssignment(subscriptionId, draft.assignmentId);
      sendNewAlert("Rate card activated successfully", AlertSeverity.Success);
    } catch (e) {
      sendNewAlert(getErrorMessage(e), AlertSeverity.Error);
    } finally {
      // Refetch on failure too: the draft may have been activated or superseded elsewhere,
      // and the stale card should give way to whatever the server now holds.
      refresh();
    }
  };

  const onRateCardCreated = () => {
    setCreateDialogOpen(false);
    sendNewAlert("Rate card created and staged as a draft", AlertSeverity.Success);
    refresh();
  };

  const actions = hasAssignment
    ? [
        <PolicyAuthorized key="assignmentActions" policies={RbacPolicies.RateCards.ReadWrite}>
          <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
            <Button variant="outlined" onClick={notImplemented}>
              Assign default
            </Button>
            <Button variant="outlined" onClick={() => setCreateDialogOpen(true)}>
              Assign new rate card
            </Button>
          </Stack>
        </PolicyAuthorized>,
      ]
    : undefined;

  return (
    <DetailsPageLayout pageName="Rate card" actions={actions}>
      {loading && !assignments && (
        <Stack spacing={1} role="status" aria-label="Loading rate card">
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="rectangular" height={120} />
        </Stack>
      )}
      {error && <Typography color="error">Failed to load the rate card for this subscription.</Typography>}
      {!error && assignments && (
        <Stack spacing={4}>
          {hasAssignment ? (
            <SubscriptionCurrentRateCard
              active={active}
              draft={draft}
              onActivateDraft={() => void onActivateDraft()}
              activating={activating}
            />
          ) : (
            <SubscriptionRateCardEmptyState
              onAssignDefault={notImplemented}
              onAssignNewRateCard={() => setCreateDialogOpen(true)}
            />
          )}
          <SubscriptionRateCardAssignmentHistory assignments={assignments} />
        </Stack>
      )}
      {subscriptionId && (
        <CreateRateCardDialog
          open={createDialogOpen}
          mode={{ kind: "subscription", subscriptionId }}
          onCancel={() => setCreateDialogOpen(false)}
          onCreated={onRateCardCreated}
        />
      )}
    </DetailsPageLayout>
  );
}
